import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Lock, Download, Utensils, CheckCircle2, Mail, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { calculateMacros, requestMealSample } from '../services/api';
import { generalj, osszeg, emakro, alkalmazottHozz } from '../lib/mealGenerator';
import { useDocumentMeta } from '../utils/useDocumentMeta';

const ACTIVITIES = [
  { value: 'sedentary_office', label: 'Ülőmunka (napi 6+ óra ülés)' },
  { value: 'standing_light', label: 'Állómunka / könnyű fizikai munka' },
  { value: 'heavy_shift', label: '12 órás műszak / nehéz fizikai munka' },
];

const GOALS = [
  { value: 'fat_loss', label: 'Fogyás', hu: 'Fogyás' },
  { value: 'maintenance', label: 'Szinten tartás', hu: 'Szintentartás' },
  { value: 'muscle_gain', label: 'Izomépítés', hu: 'Izomépítés' },
];

const hu = (n) => Math.round(n).toLocaleString('hu-HU');

const buildDayPayload = (terv) => {
  const day = terv[0];
  const total = osszeg(day.meals);
  return {
    total: { kcal: total.kcal, p: total.p, ch: total.ch, zs: total.zs },
    meals: day.meals.map((m) => {
      const em = emakro(m);
      const koret = m.koret ? m.koret.g / 100 : 0;
      return {
        slot: m.slot.cim,
        name: m.r.nev,
        kcal: em.kcal * m.f + (m.koret ? m.koret.kcal * koret : 0),
        p: em.p * m.f + (m.koret ? m.koret.p * koret : 0),
        ch: em.ch * m.f + (m.koret ? m.koret.ch * koret : 0),
        zs: em.zs * m.f + (m.koret ? m.koret.zs * koret : 0),
        prep: m.r.elk || '',
        ingredients: [
          ...alkalmazottHozz(m).map((x) => (x.qty ? `${x.qty}${x.unit} ${x.nev}` : x.nev)),
          ...(m.koret ? [`${m.koret.g}g ${m.koret.nev}`] : []),
        ],
      };
    }),
  };
};

const MealSamplePage = () => {
  useDocumentMeta({
    title: '1 napos minta étrend ingyen — Orosz Coaching',
    description:
      'Add meg az adataidat, kiszámolom a napi kalóriád és a makróid, a generátorom pedig összerak egy teljes napot grammos hozzávalókkal. A PDF-et e-mailben kapod meg.',
  });

  const [form, setForm] = useState({
    gender: 'male', age: '', height_cm: '', weight_kg: '',
    activity_level: 'sedentary_office', goal: 'fat_loss',
    veget: false, vegan: false, laktoz: false,
  });
  const [generating, setGenerating] = useState(false);
  const [macros, setMacros] = useState(null);
  const [terv, setTerv] = useState(null);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [pdf, setPdf] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const dayPayload = useMemo(() => (terv ? buildDayPayload(terv) : null), [terv]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const m = await calculateMacros({
        gender: form.gender,
        age: Number(form.age),
        height_cm: Number(form.height_cm),
        weight_kg: Number(form.weight_kg),
        activity_level: form.activity_level,
        goal: form.goal,
      });
      const goalHu = GOALS.find((g) => g.value === form.goal)?.hu || 'Szintentartás';
      const target = {
        kcal: m.target_kcal,
        feherje: m.protein_g,
        zsir: m.fat_g,
        szenhidrat: m.carbs_g,
      };
      const plan = generalj(target, goalHu, 1, {
        diet: { veget: form.veget, vegan: form.vegan, laktoz: form.laktoz },
        konyhak: [],
        feherjedus: false,
        edzes: [false, false, false, false, false, false, false],
      });
      setMacros(m);
      setTerv(plan);
      setUnlocked(false);
      setPdf(null);
      setTimeout(() => {
        document.getElementById('minta-eredmeny')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A számítás nem sikerült. Ellenőrizd az adatokat.');
    } finally {
      setGenerating(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await requestMealSample({
        email: email.trim(),
        name: '',
        inputs: {
          gender: form.gender,
          age: Number(form.age),
          height_cm: Number(form.height_cm),
          weight_kg: Number(form.weight_kg),
          activity_level: form.activity_level,
          goal: form.goal,
          diet: { veget: form.veget, vegan: form.vegan, laktoz: form.laktoz },
        },
        macros: {
          target_kcal: macros.target_kcal,
          protein_g: macros.protein_g,
          carbs_g: macros.carbs_g,
          fat_g: macros.fat_g,
          activity_label: macros.activity_label,
        },
        day: dayPayload,
      });
      setPdf({ base64: res.pdf_base64, filename: res.filename });
      setUnlocked(true);
      toast.success('Elkészült! A PDF-et e-mailben is elküldtem.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A kérés nem sikerült. Próbáld újra.');
    } finally {
      setSending(false);
    }
  };

  const downloadPdf = () => {
    if (!pdf) return;
    const bin = atob(pdf.base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = pdf.filename || 'minta-etrend.pdf';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  return (
    <div data-testid="meal-sample-page" className="min-h-screen bg-[#0B1B33] text-white">
      <header className="container mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" data-testid="meal-sample-brand" className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          <span className="text-white">OROSZ</span><span className="text-yellow-500 ml-1">COACHING</span>
        </Link>
        <Link to="/" data-testid="meal-sample-back" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
          <ArrowLeft size={16} /> Főoldal
        </Link>
      </header>

      <main className="container mx-auto px-6 pb-20">
        <div className="max-w-5xl grid lg:grid-cols-[minmax(0,1fr)_380px] gap-10 lg:gap-16">
          {/* Bal: bevezető + űrlap */}
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-bold text-yellow-400 tracking-[0.28em] mb-5">
              <Sparkles size={14} /> INGYENES MINTA
            </div>
            <h1
              data-testid="meal-sample-heading"
              className="text-4xl sm:text-5xl font-black leading-[1.05] mb-5"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              1 napos étrend,
              <span className="block text-yellow-500">a te adataidra számolva</span>
            </h1>
            <p className="text-base md:text-lg text-white/70 leading-relaxed mb-10 max-w-xl">
              Kiszámolom a napi kalóriádat és a makróidat, majd az étrendgenerátorom összerak egy
              teljes napot: reggeli, ebéd, vacsora, kiegészítő és desszert — grammos hozzávalókkal.
              A PDF-et az e-mail címedre küldöm, és itt is letöltheted.
            </p>

            <form onSubmit={handleGenerate} data-testid="meal-sample-form" className="space-y-5 max-w-xl">
              <div>
                <div className="text-xs font-bold tracking-widest text-white/50 mb-2">NEM</div>
                <div className="flex gap-2">
                  {[{ v: 'male', l: 'Férfi' }, { v: 'female', l: 'Nő' }].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      data-testid={`meal-sample-gender-${o.v}`}
                      onClick={() => set('gender', o.v)}
                      className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-colors ${
                        form.gender === o.v
                          ? 'bg-yellow-500 text-[#0B1B33]'
                          : 'bg-white/5 text-white/70 hover:bg-white/10'
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { k: 'age', l: 'KOR (ÉV)', min: 14, max: 99 },
                  { k: 'height_cm', l: 'MAGASSÁG (CM)', min: 120, max: 230 },
                  { k: 'weight_kg', l: 'TESTSÚLY (KG)', min: 35, max: 250 },
                ].map((f) => (
                  <div key={f.k}>
                    <div className="text-xs font-bold tracking-widest text-white/50 mb-2">{f.l}</div>
                    <Input
                      data-testid={`meal-sample-${f.k}`}
                      type="number"
                      required
                      min={f.min}
                      max={f.max}
                      step="0.1"
                      value={form[f.k]}
                      onChange={(e) => set(f.k, e.target.value)}
                      className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/30"
                    />
                  </div>
                ))}
              </div>

              <div>
                <div className="text-xs font-bold tracking-widest text-white/50 mb-2">MUNKAVÉGZÉS / AKTIVITÁS</div>
                <select
                  data-testid="meal-sample-activity"
                  value={form.activity_level}
                  onChange={(e) => set('activity_level', e.target.value)}
                  className="w-full h-12 rounded-lg bg-white/5 border border-white/15 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                >
                  {ACTIVITIES.map((a) => (
                    <option key={a.value} value={a.value} className="bg-[#0B1B33]">{a.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="text-xs font-bold tracking-widest text-white/50 mb-2">CÉL</div>
                <div className="flex flex-wrap gap-2">
                  {GOALS.map((g) => (
                    <button
                      key={g.value}
                      type="button"
                      data-testid={`meal-sample-goal-${g.value}`}
                      onClick={() => set('goal', g.value)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-bold transition-colors ${
                        form.goal === g.value
                          ? 'bg-yellow-500 text-[#0B1B33]'
                          : 'bg-white/5 text-white/70 hover:bg-white/10'
                      }`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs font-bold tracking-widest text-white/50 mb-2">ÉTRENDI IGÉNY (OPCIONÁLIS)</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { k: 'veget', l: 'Vegetáriánus' },
                    { k: 'vegan', l: 'Vegán' },
                    { k: 'laktoz', l: 'Laktózmentes' },
                  ].map((d) => (
                    <button
                      key={d.k}
                      type="button"
                      data-testid={`meal-sample-diet-${d.k}`}
                      onClick={() => set(d.k, !form[d.k])}
                      className={`px-4 py-2.5 rounded-lg text-sm font-semibold border transition-colors ${
                        form[d.k]
                          ? 'bg-emerald-500/15 border-emerald-400/60 text-emerald-300'
                          : 'bg-white/5 border-white/15 text-white/70 hover:bg-white/10'
                      }`}
                    >
                      {d.l}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                data-testid="meal-sample-generate-btn"
                type="submit"
                disabled={generating}
                className="w-full bg-yellow-500 hover:bg-yellow-400 text-[#0B1B33] font-black tracking-wide h-14 text-base"
              >
                {generating
                  ? <><Loader2 size={18} className="mr-2 animate-spin" /> Számolok…</>
                  : <><Utensils size={18} className="mr-2" /> ÖSSZERAKOM AZ ÉTRENDEM</>}
              </Button>
            </form>
          </div>

          {/* Jobb: eredmény */}
          <div id="minta-eredmeny" className="lg:sticky lg:top-8 lg:self-start">
            {!macros && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-sm text-white/60 leading-relaxed">
                <div className="text-xs font-bold tracking-widest text-yellow-400 mb-3">MIT KAPSZ?</div>
                <ul className="space-y-2.5">
                  {[
                    'A napi kalóriád és a makróid (fehérje, szénhidrát, zsír)',
                    '5 étkezés konkrét fogásokkal, grammos hozzávalókkal',
                    'Letölthető PDF, amit e-mailben is megkapsz',
                  ].map((t) => (
                    <li key={t} className="flex gap-2.5">
                      <CheckCircle2 size={16} className="text-yellow-500 shrink-0 mt-0.5" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {macros && dayPayload && (
              <div data-testid="meal-sample-result" className="rounded-2xl border border-yellow-500/30 bg-white/[0.04] overflow-hidden">
                <div className="p-5 border-b border-white/10">
                  <div className="text-xs font-bold tracking-widest text-yellow-400 mb-2">NAPI CÉLOD</div>
                  <div data-testid="meal-sample-kcal" className="text-3xl font-black" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {hu(macros.target_kcal)} kcal
                  </div>
                  <div className="text-sm text-white/70 mt-1.5">
                    Fehérje {macros.protein_g} g · Szénhidrát {macros.carbs_g} g · Zsír {macros.fat_g} g
                  </div>
                </div>

                <div className="p-5 space-y-3">
                  {dayPayload.meals.map((m, i) => (
                    <div key={i} data-testid={`meal-sample-meal-${i}`} className="rounded-lg bg-white/[0.04] p-3.5">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="text-[10px] font-bold tracking-widest text-yellow-500">{m.slot.toUpperCase()}</div>
                        <div className="text-xs text-white/50">{hu(m.kcal)} kcal</div>
                      </div>
                      <div className="text-sm font-semibold mt-1">{m.name}</div>
                      {unlocked ? (
                        <>
                          <div className="text-xs text-white/65 mt-1.5 leading-relaxed">
                            {m.ingredients.join(', ')}
                          </div>
                          {m.prep && (
                            <details className="mt-2 text-xs text-white/55" data-testid={`meal-sample-prep-${i}`}>
                              <summary className="cursor-pointer text-yellow-500/90 font-semibold">Elkészítés</summary>
                              <p className="mt-1.5 leading-relaxed whitespace-pre-line text-white/65">{m.prep}</p>
                            </details>
                          )}
                        </>
                      ) : (
                        <div className="text-xs text-white/35 mt-1.5 select-none blur-[3px]">
                          {m.ingredients.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="p-5 border-t border-white/10 bg-[#101f3a]">
                  {unlocked ? (
                    <div data-testid="meal-sample-unlocked">
                      <div className="flex items-center gap-2 text-emerald-400 font-bold text-sm mb-3">
                        <CheckCircle2 size={16} /> Kész — a PDF-et e-mailben is elküldtem
                      </div>
                      <Button
                        data-testid="meal-sample-download-btn"
                        onClick={downloadPdf}
                        className="w-full bg-yellow-500 hover:bg-yellow-400 text-[#0B1B33] font-black h-12"
                      >
                        <Download size={17} className="mr-2" /> PDF LETÖLTÉSE
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleEmailSubmit} data-testid="meal-sample-email-form">
                      <div className="flex items-center gap-2 text-sm font-bold text-white mb-1.5">
                        <Lock size={15} className="text-yellow-500" />
                        A hozzávalók és a PDF e-mailben
                      </div>
                      <p className="text-xs text-white/55 mb-3 leading-relaxed">
                        Add meg az e-mail címed, és azonnal megkapod a teljes napot PDF-ben. Soha nem
                        adom tovább a címed, egy kattintással leiratkozhatsz.
                      </p>
                      <Input
                        data-testid="meal-sample-email-input"
                        type="email"
                        required
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="te@email.hu"
                        className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/30 mb-3"
                      />
                      <Button
                        data-testid="meal-sample-email-submit"
                        type="submit"
                        disabled={sending}
                        className="w-full bg-yellow-500 hover:bg-yellow-400 text-[#0B1B33] font-black h-12"
                      >
                        {sending
                          ? <><Loader2 size={17} className="mr-2 animate-spin" /> Küldöm…</>
                          : <><Mail size={17} className="mr-2" /> KÉREM A PDF-ET</>}
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-white/40 mt-12 max-w-3xl leading-relaxed">
          Ez egy minta: egyetlen nap, tájékoztató jelleggel. A teljes, 7 napos étrendet — heti
          cserékkel, bevásárlólistával és az edzéseidhez igazítva — a coachingban kapod meg, az
          anamnézised és a méréseid alapján.{' '}
          <Link to="/#programok" className="text-yellow-500 hover:underline">Nézd meg a programokat</Link>
        </p>
      </main>
    </div>
  );
};

export default MealSamplePage;
